import type { ComponentType } from 'react'
import type { Integration } from '@/types/integration'
import type { ExtensionDefinition, ExtensionRegistry, ExtensionViewerProps, ExtensionInfoPaneProps } from '@/types/extension'
import { mockIntegration } from '@/components/integrations/mock-integration'
import { basecampIntegration } from '@/components/integrations/basecamp'
import { voiceMemosIntegration } from '@/components/integrations/voice-memos'
import { knowledgeHubIntegration } from '@/components/integrations/knowledge-hub'

// Extension viewer components
import { BasecampTodoViewer } from '@/components/integrations/basecamp/basecamp-todo-viewer'
import { BasecampTodoInfoPane } from '@/components/integrations/basecamp/basecamp-todo-info-pane'

// Future integrations:
// import { googleDriveIntegration } from '@/components/integrations/googledrive'
// import { gitlabIntegration } from '@/components/integrations/gitlab'

/**
 * Central registry of all available integrations
 *
 * Each integration exports a configuration object that implements
 * the Integration interface. Add new integrations here.
 */
export const AVAILABLE_INTEGRATIONS: Integration[] = [
  mockIntegration,         // Test integration (can be removed in production)
  basecampIntegration,     // Basecamp integration ✅
  voiceMemosIntegration,   // Voice Memos integration ✅
  knowledgeHubIntegration, // Knowledge Hub review queue ✅
  // googleDriveIntegration, // Phase 2
  // gitlabIntegration,      // Phase 2
]

/**
 * Extension Registry
 *
 * Extensions register their viewer components, tab types, and capabilities here.
 * This enables dynamic rendering in document-viewer without hard-coding.
 *
 * When adding a new extension:
 * 1. Create extension components (viewer, info pane, etc.)
 * 2. Register here with tab types it handles
 * 3. Core components will automatically use it
 */
export const REGISTERED_EXTENSIONS: ExtensionRegistry = [
  /**
   * Basecamp Extension
   * Handles Basecamp todo views and todolist views
   */
  {
    id: 'basecamp',
    name: 'Basecamp',
    tabTypes: [
      'integration-basecamp',
      'integration-basecamp-todo',
      'integration-basecamp-todo-list',
    ],
    ViewerComponent: BasecampTodoViewer as unknown as ComponentType<ExtensionViewerProps>,
    InfoPaneComponent: BasecampTodoInfoPane as unknown as ComponentType<ExtensionInfoPaneProps>,
    enabled: true,
  },

  /**
   * Voice Memos Extension
   * Handles voice memo views (future implementation)
   */
  {
    id: 'voice-memos',
    name: 'Voice Memos',
    tabTypes: ['integration-voice-memos'],
    // ViewerComponent: VoiceMemosViewer, // TODO: Create voice memos viewer
    // InfoPaneComponent: VoiceMemosInfoPane, // Optional
    enabled: true,
  },

  // Future extensions:
  // {
  //   id: 'slack',
  //   name: 'Slack',
  //   tabTypes: ['integration-slack-channel', 'integration-slack-thread'],
  //   ViewerComponent: SlackViewer,
  //   enabled: true,
  // },
]

/**
 * Get integration by ID
 */
export const getIntegrationById = (id: string): Integration | undefined => {
  return AVAILABLE_INTEGRATIONS.find(integration => integration.id === id)
}

/**
 * Get all enabled integrations
 */
export const getEnabledIntegrations = (): Integration[] => {
  return AVAILABLE_INTEGRATIONS.filter(integration => integration.enabled)
}

/**
 * Get all configured integrations (enabled + has credentials)
 */
export const getConfiguredIntegrations = (): Integration[] => {
  return AVAILABLE_INTEGRATIONS.filter(
    integration => integration.enabled && integration.configured
  )
}

/**
 * Get extension by ID
 */
export const getExtensionById = (id: string): ExtensionDefinition | undefined => {
  return REGISTERED_EXTENSIONS.find(extension => extension.id === id)
}

/**
 * Get extension for specific tab type
 */
export const getExtensionForTabType = (
  tabType: string
): ExtensionDefinition | undefined => {
  return REGISTERED_EXTENSIONS.find(extension =>
    extension.tabTypes.includes(tabType as any)
  )
}

/**
 * Check if tab type is handled by an extension
 */
export const isExtensionTabType = (tabType: string): boolean => {
  return REGISTERED_EXTENSIONS.some(extension =>
    extension.tabTypes.includes(tabType as any)
  )
}


